import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { CONTAINER_DEADLINE_KEYS, KIND_SUGGESTIONS, normalizeTags, parentOf, shortUuid, type Container, type ContainerInput } from "@egalregal/shared";
import { useAppStore, type PendingUpload } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { CaptureToolbar, type StagedCapture } from "../capture/CaptureToolbar";
import { fileNames } from "../capture/captureFileName";
import { OwnerSuggestions } from "./OwnerSuggestions";
import { TagAutocomplete } from "./TagAutocomplete";
import { PendingUploadList } from "./PendingUploadList";
import {
  DatePropertyFields,
  dateValuesOf,
  mergeDateProperties,
  type DatePropertyValues,
} from "../../shared/components/DatePropertyFields";
import { renderMarkdown } from "../../shared/lib/markdown";
import { ConflictError } from "../../shared/api";
import { log } from "../../logger";
import { useDiscardGuard, useUnsavedChanges } from "../shell/discardGuard";
import { ContainerEntrySelector, ownEntryForContainer } from "../catalogs/ContainerEntrySelector";
import { ConfirmDeleteDialog } from "../items/ConfirmDeleteDialog";
import { linkUnderParent } from "./link-child";
import { resolveCreateEntry } from "./create-entry";
import { QuickAddBar } from "./QuickAddBar";

/** A staged file in the form: display fields plus the File to upload (ticket 11). */
interface StagedFile extends PendingUpload {
  name: string;
  size: number;
}

/**
 * Tags offered by the autocomplete (ticket 24): every existing tag plus the
 * recent ones, case-normalized and deduped, recency first.
 */
export function tagOptions(containers: readonly Container[]): string[] {
  const byRecency = [...containers]
    .filter((c) => c.tags.length > 0)
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  const recent = normalizeTags(byRecency.slice(0, 8).flatMap((c) => c.tags));
  const all = normalizeTags(byRecency.flatMap((c) => c.tags));
  return [...recent, ...all.filter((tag) => !recent.includes(tag))];
}

/**
 * Owners of existing containers, most-recently-used first (by updatedAt),
 * deduped case-insensitively keeping the most recent spelling, capped at six —
 * quick-fill chips so the latest owner can be set with correct spelling.
 */
export function ownerOptions(containers: readonly Container[]): string[] {
  const byRecency = [...containers]
    .filter((c) => c.owner && c.owner.trim().length > 0)
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const container of byRecency) {
    const owner = container.owner!.trim();
    const key = owner.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(owner);
    if (unique.length === 6) break;
  }
  return unique;
}

/**
 * Shared create/edit form (ticket 05). Metadata fields plus the three capture
 * CTAs — Camera, Record, File — from the ticket-14 verdict (one tap each, no
 * sheet): Camera opens the camera dialog (the single photo-capture surface,
 * ux-polish 03) which stages its stack into the pending list; Record opens
 * the memo-only live-capture panel (ticket 13); File opens the device file
 * browser. Staged media uploads as files once the container is saved
 * (ticket 11). An `initialId` (ticket 30) comes from a scanned pre-printed
 * label and becomes the container's UUID. Tags are typed via an autocomplete
 * over recent + existing tags (ticket 24) and saved case-normalized.
 */
export function ContainerForm({ initial: initialProp }: { initial?: Container }) {
  const { initialId, editingId } = useParams<{ initialId?: string; editingId?: string }>();
  const [searchParams] = useSearchParams();
  // The merged view's Add sub-container opens `/add?parent=<id>` (and keeps a
  // scanned `/add/:initialId` label). The parent only scopes a create; edit
  // ignores it.
  const parentId = searchParams.get("parent") ?? undefined;
  const navigate = useNavigate();
  const t = useT();
  const openContainer = useAppStore((s) => s.openContainer);
  const containers = useAppStore((s) => s.containers);
  const initial = initialProp ?? (editingId ? containers.find((c) => c.id === editingId) : undefined);
  const containerId = initialProp?.id ?? editingId;

  // A reload or deep link starts with an empty store: resolve the container
  // (scoping its catalog entry) before mounting the form, so the fields are
  // initialised from the stored values instead of an empty draft.
  useEffect(() => {
    if (!editingId || initialProp || initial) return;
    let cancelled = false;
    void openContainer(editingId).then((result) => {
      if (!cancelled && result === "missing") navigate("/browse");
    });
    return () => {
      cancelled = true;
    };
  }, [editingId, initialProp, initial, openContainer, navigate]);

  if (containerId && !initial) {
    return (
      <Typography variant="body1" color="text.secondary">
        {t("loading")}
      </Typography>
    );
  }

  // Keyed by container: switching edit targets (or arriving from a reload)
  // starts a fresh draft instead of keeping the previous container's fields.
  return <ContainerFormBody key={containerId ?? "new"} initial={initial} initialId={initialId} parentId={parentId} />;
}

function ContainerFormBody({
  initial,
  initialId,
  parentId,
}: {
  initial?: Container;
  initialId?: string;
  parentId?: string;
}) {
  const navigate = useNavigate();
  const t = useT();
  const saveContainer = useAppStore((s) => s.saveContainer);
  const assignEntry = useAppStore((s) => s.assignEntry);
  const removeContainer = useAppStore((s) => s.removeContainer);
  const containers = useAppStore((s) => s.containers);
  const catalogContainers = useAppStore((s) => s.catalogContainers);
  const currentLocationId = useAppStore((s) => s.currentLocationId);
  const ownerSuggestions = ownerOptions(containers);
  // The readable union resolves a parent that the selected entry's slice does
  // not hold (an explicit `?parent` from another entry); the slice wins when
  // both know the container because it carries this session's optimistic edits.
  const readableContainers = readableUnion(containers, catalogContainers);
  const findContainer = (id: string) => readableContainers.find((c) => c.id === id);
  // Create-only: the parent scoping this form (container-view 05). Editing keeps
  // the container where it is; moving it is the Move action. Quick-add (ticket
  // 12) is the default parent only when the caller named no parent; an
  // explicit `?parent` (Add sub-container / scan) is a one-off override, and
  // an edit never links.
  const explicitParentId = initial ? undefined : parentId;
  const autoLinkId = initial || explicitParentId !== undefined ? undefined : currentLocationId;
  const linkParentId = explicitParentId ?? autoLinkId;
  const linkParentLabel =
    linkParentId === undefined
      ? undefined
      : (findContainer(linkParentId)?.name ?? shortUuid(linkParentId));

  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  // The create form only ever creates roots — nested containers are linked
  // into the tree afterwards, so there is no parent path here and the picker
  // always applies. Edit mode shows the owning entry below instead (see
  // `ownEntry`), because its current assignment follows the tree position.
  const target = initial ? undefined : resolveCreateEntry(entries, sharedWithMe, selectedEntryId);
  const [pickedEntryId, setPickedEntryId] = useState<string | undefined>(undefined);
  // A create and its link must share one home — the server rejects a link that
  // crosses homes. The parent therefore decides the entry: a picked entry can
  // only stand when it is the parent's own entry. Auto-link hides the picker,
  // so the target cannot be retargeted away from the location's entry.
  const parentEntryId =
    linkParentId === undefined
      ? undefined
      : ownEntryForContainer(entries, readableContainers, linkParentId)?.id;
  // While auto-link is active the picker is hidden, so a pick made before it
  // was set must not keep a stale entry target.
  const pickedTargetId = autoLinkId === undefined ? pickedEntryId : undefined;
  const targetEntryId = parentEntryId ?? pickedTargetId ?? target?.entryId;
  const targetLabel = target?.list.find((c) => c.id === targetEntryId)?.label;
  // A parent-derived entry is not the fallback the note explains.
  const showFallbackNote =
    target?.fallback === true &&
    pickedTargetId === undefined &&
    parentEntryId === undefined &&
    targetLabel !== undefined;

  // An edit shows the entry that owns the container (the same derivation the
  // retired detail-page selector used). Containers inside a shared entry match
  // no own root and keep their assignment; relocating them is Move's job.
  const ownEntry = initial ? ownEntryForContainer(entries, containers, initial.id) : undefined;
  const [pickedAssignmentId, setPickedAssignmentId] = useState<string | undefined>(undefined);
  const assignedEntryId = pickedAssignmentId ?? ownEntry?.id;
  const assignmentChanged = ownEntry !== undefined && assignedEntryId !== ownEntry.id;

  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [owner, setOwner] = useState(initial?.owner ?? "");
  const [kind, setKind] = useState(initial?.kind ?? "box");
  const [tags, setTags] = useState<string[]>(initial ? normalizeTags(initial.tags) : []);
  // Containers carry expires-on only (deadlines 03); the form edits the same
  // typed bag the item form does, with the container vocabulary.
  const [dates, setDates] = useState<DatePropertyValues>(() =>
    dateValuesOf(initial?.properties, CONTAINER_DEADLINE_KEYS),
  );
  const [pending, setPending] = useState<StagedFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [descriptionPreview, setDescriptionPreview] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState<FormNotice | null>(null);
  // The last saved revision. A failed entry assignment leaves the user on the
  // form after a successful metadata save, so later saves and the dirty check
  // must start from what the server now holds — not the stale mount prop.
  const [baseline, setBaseline] = useState(initial);
  const guard = useDiscardGuard();

  // The shell owns the prompt; the form only says whether the draft differs
  // from what is stored (spec §3). A new container is not guarded: there is no
  // stored revision to fall back to, and the task view's exit is its cancel.
  // A changed catalog entry is unsaved too: it is applied only on Save.
  const dirty =
    baseline !== undefined &&
    (name !== baseline.name ||
      description !== (baseline.description ?? "") ||
      owner !== (baseline.owner ?? "") ||
      kind !== baseline.kind ||
      tagsDiffer(tags, normalizeTags(baseline.tags)) ||
      dateValuesDiffer(dates, dateValuesOf(baseline.properties, CONTAINER_DEADLINE_KEYS)) ||
      pending.length > 0 ||
      assignmentChanged);
  useUnsavedChanges(dirty);

  /** Place every captured media into the staged list (ticket 11). */
  const keepStaged = (capture: StagedCapture) => {
    setPending((prev) => [
      ...prev,
      {
        name: capture.file.name,
        size: capture.file.size,
        kind: capture.kind,
        file: capture.file,
        location: capture.location,
      },
    ]);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const input: ContainerInput = {
      kind: kind.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      owner: owner.trim() || undefined,
      tags: normalizeTags(tags),
      properties: mergeDateProperties(baseline?.properties, dates, CONTAINER_DEADLINE_KEYS),
    };
    try {
      const saved = await saveContainer(input, {
        editingId: baseline?.id,
        version: baseline?.version,
        pending: pending.map((p) => ({ file: p.file, kind: p.kind, location: p.location })),
        entryId: initial ? undefined : targetEntryId,
        // The scanned label arrives through the `/add/:initialId` route.
        createId: initialId,
      });
      // The save is authoritative: a later assignment failure must not make the
      // form re-upload staged files or resend the old version.
      setBaseline(saved);
      setPending([]);

      // Entry assignment is a separate step (container-view 04): only after the
      // metadata save succeeded, and its failure keeps the user on the form.
      if (initial && ownEntry && assignedEntryId && assignedEntryId !== ownEntry.id) {
        try {
          await assignEntry(saved.id, assignedEntryId, { version: saved.version });
          setPickedAssignmentId(undefined);
        } catch (err) {
          log.warn(
            { err, containerId: saved.id, entryId: assignedEntryId },
            "Failed to assign the container to its catalog entry",
          );
          setNotice({
            message: t("saveEntryError", {
              error: err instanceof Error ? err.message : String(err),
            }),
            severity: "warning",
          });
          return;
        }
      }

      // A parent-scoped create links the new root under its parent after the
      // create succeeded (container-view 05). A failed link is a partial
      // outcome: the container stays at the top level and stays reachable.
      if (linkParentId) {
        try {
          await linkUnderParent(linkParentId, saved.id);
        } catch (err) {
          log.warn(
            { err, containerId: saved.id, parentId: linkParentId },
            "Created the container but failed to link it under its parent",
          );
          setNotice({
            message: t("createLinkError", {
              parent: linkParentLabel ?? shortUuid(linkParentId),
              error: err instanceof Error ? err.message : String(err),
            }),
            severity: "warning",
            openContainerId: saved.id,
          });
          return;
        }
        setNotice({ message: t("created"), severity: "success" });
        navigate(`/browse/${linkParentId}`, { replace: true });
        return;
      }

      setNotice({ message: initial ? t("saved") : t("created"), severity: "success" });
      // Show the saved container, replacing the task/edit entry: Back must not
      // return to the form that was just submitted (spec §3 task exits).
      navigate(`/browse/${saved.id}`, { replace: true });
    } catch (err) {
      // A scanned label that already exists (ticket 30): offer the container
      // instead of failing silently — the 409 means a box with this UUID is
      // already in the catalog.
      if (err instanceof ConflictError && initialId) {
        setConflict(true);
      } else {
        log.warn({ err }, "Failed to save container");
        setNotice({
          message: t("saveError", {
            error: err instanceof Error ? err.message : String(err),
          }),
          severity: "error",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  /** Delete from the edit form: confirm, then land on the parent level. */
  const handleDelete = async () => {
    if (!initial) return;
    const parent = parentOf(containers, initial.id);
    setBusy(true);
    try {
      await removeContainer(initial.id, baseline?.version ?? initial.version, initial.children.length > 0);
      setConfirmDelete(false);
      navigate(parent ? `/browse/${parent.id}` : "/browse", { replace: true });
    } catch (err) {
      log.warn({ err, containerId: initial.id }, "Failed to delete container");
      setConfirmDelete(false);
      setNotice({
        message: t("deleteError", {
          error: err instanceof Error ? err.message : String(err),
        }),
        severity: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const openExisting = () => {
    if (initialId) navigate(`/browse/${initialId}`);
  };

  const cancel = () => {
    // Cancel is an exit like the back link: the shell asks first when the
    // edited container has unsaved changes (spec §3).
    guard.request(() => {
      if (initial) navigate(`/browse/${initial.id}`);
      else navigate("/browse");
    });
  };

  // Saved files carry the stored stems; pending files keep their picked or
  // captured names. Both must feed collision detection so a same-second
  // capture after Save cannot reuse a stored name.
  const captureUsedNames = [...fileNames(baseline?.files ?? []), ...fileNames(pending)];

  return (
    <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!initial && (
        <QuickAddBar
          overrideParentLabel={explicitParentId === undefined ? undefined : linkParentLabel}
        />
      )}
      {initialId && !conflict && (
        <Typography variant="body2" color="text.secondary">
          {t("createByScan", { id: shortUuid(initialId) })}
        </Typography>
      )}
      {explicitParentId && linkParentLabel && (
        <Typography variant="body2" color="text.secondary" data-testid="create-parent-note">
          {t("createAsChild", { parent: linkParentLabel })}
        </Typography>
      )}
      {conflict && (
        <Box component="div" data-testid="create-conflict">
          <Typography variant="body2" color="warning.main">
            {t("createConflict")}
          </Typography>
          <Button variant="outlined" size="small" onClick={openExisting} sx={{ mt: 1 }}>
            {t("openInstead")}
          </Button>
        </Box>
      )}

      <TextField
        label={t("formName")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <Autocomplete
        freeSolo
        options={KIND_SUGGESTIONS}
        value={kind}
        onChange={(_e, value) => setKind(value ?? "")}
        onInputChange={(_e, value) => setKind(value)}
        renderInput={(params) => <TextField {...params} label={t("formKind")} required />}
      />
      {target && target.list.length > 1 && autoLinkId === undefined && (
        <TextField
          select
          label={t("formEntry")}
          value={targetEntryId ?? ""}
          onChange={(e) => setPickedEntryId(e.target.value)}
          data-testid="create-entry-picker"
        >
          {target.list.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {showFallbackNote && (
        <Typography variant="body2" color="text.secondary" data-testid="create-entry-fallback">
          {t("createEntryFallback", { entry: targetLabel ?? "" })}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {t("formDescription")}
        </Typography>
        <Button size="small" onClick={() => setDescriptionPreview((p) => !p)}>
          {descriptionPreview ? t("edit") : t("preview")}
        </Button>
      </Box>
      {descriptionPreview ? (
        <Box component="div" data-testid="description-preview" sx={{ "& p": { my: 0.5 } }}>
          {renderMarkdown(description)}
        </Box>
      ) : (
        <TextField
          aria-label={t("formDescription")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
        />
      )}
      <TextField
        label={t("formOwner")}
        value={owner}
        onChange={(e) => setOwner(e.target.value)}
      />
      <OwnerSuggestions suggestions={ownerSuggestions} onSelect={setOwner} />
      {/* The edit form owns the own-entry assignment; a shared entry keeps its
          container and renders nothing here (container-view 04). */}
      {initial && (
        <ContainerEntrySelector
          containerId={initial.id}
          value={assignedEntryId}
          onChange={setPickedAssignmentId}
        />
      )}
      <TagAutocomplete options={tagOptions(containers)} value={tags} onChange={setTags} />

      <DatePropertyFields
        keys={CONTAINER_DEADLINE_KEYS}
        values={dates}
        onChange={setDates}
      />

      <CaptureToolbar onStaged={keepStaged} usedNames={captureUsedNames} />

      <PendingUploadList pending={pending} onRemove={(index) => setPending((prev) => prev.filter((_, j) => j !== index))} />

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button onClick={cancel}>{t("cancel")}</Button>
        <Button type="submit" variant="contained" disabled={busy || !name.trim() || !kind.trim()}>
          {t("save")}
        </Button>
      </Box>

      {/* Destructive concern below the form's actions (container-view 04). */}
      {initial && (
        <Box
          data-testid="delete-container-section"
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography component="span" variant="subtitle2" color="error.main">
            {t("deleteSectionTitle")}
          </Typography>
          <Button color="error" variant="outlined" disabled={busy} onClick={() => setConfirmDelete(true)}>
            {t("delete")}
          </Button>
        </Box>
      )}

      {initial && (
        <ConfirmDeleteDialog
          open={confirmDelete}
          childrenCount={initial.children.length}
          onClose={() => setConfirmDelete(false)}
          onDelete={() => void handleDelete()}
        />
      )}

      <Snackbar
        open={notice !== null}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notice?.severity ?? "success"}
          onClose={() => setNotice(null)}
          action={
            notice?.openContainerId ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`/browse/${notice.openContainerId}`, { replace: true })}
              >
                {t("openContainer")}
              </Button>
            ) : undefined
          }
        >
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

interface FormNotice {
  message: string;
  severity: "success" | "warning" | "error";
  /** A saved container whose follow-up step failed; offers a way to open it. */
  openContainerId?: string;
}

/**
 * Merge the selected entry's slice with the readable union, the slice winning
 * on duplicate ids. A parent outside the selected entry is only in the union;
 * an optimistically edited container is only in the slice.
 */
function readableUnion(
  containers: readonly Container[],
  catalogContainers: readonly Container[],
): Container[] {
  const byId = new Map<string, Container>();
  for (const container of catalogContainers) byId.set(container.id, container);
  for (const container of containers) byId.set(container.id, container);
  return [...byId.values()];
}

/** Tags are stored normalized; a draft matching the stored set is not a change. */
function tagsDiffer(draft: readonly string[], stored: readonly string[]): boolean {
  return draft.length !== stored.length || draft.some((tag, index) => tag !== stored[index]);
}

/** The date bag is sparse; a key that is absent and one set to "" are equal. */
function dateValuesDiffer(draft: DatePropertyValues, stored: DatePropertyValues): boolean {
  const keys = new Set([...Object.keys(draft), ...Object.keys(stored)]);
  return [...keys].some((key) => (draft[key] ?? "") !== (stored[key] ?? ""));
}
